import React from 'react';
import {
  Box,
  Text,
  Textarea,
  Button,
  Flex,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSave, FiAlertTriangle } from 'react-icons/fi';
import { generatePhoneticText } from '../../../scripts/unifiedNarrationSync';

// Methodist brand colors
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';

interface NarrationEditorProps {
  narrationText: string;
  phoneticText: string;
  onNarrationChange: (text: string) => void;
  onPhoneticChange: (text: string) => void;
  onSave: () => void;
  isLoading?: boolean;
  hasConflict?: boolean;
}

const NarrationEditor: React.FC<NarrationEditorProps> = ({
  narrationText,
  phoneticText,
  onNarrationChange,
  onPhoneticChange,
  onSave,
  isLoading = false,
  hasConflict = false,
}) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontWeight="bold" fontSize="lg" color={methodistNavy}>Narration Editor</Text>
        
        <Flex align="center">
          {hasConflict && (
            <Tooltip label="This narration has conflicts with the version in Firebase">
              <Badge colorScheme="orange" mr={2}>
                <Flex align="center">
                  <Icon as={FiAlertTriangle} mr={1} />
                  Conflict Detected
                </Flex>
              </Badge>
            </Tooltip>
          )}
          <Button
            leftIcon={<Icon as={FiSave} />}
            colorScheme="blue"
            onClick={onSave}
            isLoading={isLoading}
            size="sm"
          >
            Save Changes
          </Button>
        </Flex>
      </Flex>
      
      <Tabs 
        flex="1" 
        display="flex" 
        flexDirection="column" 
        variant="enclosed"
        colorScheme="blue"
        isLazy
        width="100%"
      >
        <TabList width="100%">
          <Tab 
            width="200px" 
            fontWeight="medium" 
            _selected={{ 
              color: methodistBlue, 
              borderColor: 'gray.200',
              borderBottomColor: 'white',
              fontWeight: 'bold'
            }}
          >
            Standard Narration
          </Tab>
          <Tab 
            width="200px" 
            fontWeight="medium"
            _selected={{ 
              color: methodistBlue, 
              borderColor: 'gray.200',
              borderBottomColor: 'white',
              fontWeight: 'bold'
            }}
          >
            Phonetic Narration
          </Tab>
          <Box flex="1" borderBottomWidth="1px" borderColor="gray.200" />
        </TabList>
        
        <TabPanels flex="1" display="flex" flexDirection="column">
          <TabPanel height="100%" padding={0} pt={4}>
            <Textarea
              value={narrationText}
              onChange={(e) => onNarrationChange(e.target.value)}
              placeholder="Enter narration text here..."
              size="md"
              height="100%"
              resize="none"
              borderColor={borderColor}
              _hover={{ borderColor: 'gray.300' }}
              _focus={{ borderColor: methodistBlue, boxShadow: `0 0 0 1px ${methodistBlue}` }}
            />
          </TabPanel>
          <TabPanel height="100%" padding={0} pt={4}>
            <Box height="100%" display="flex" flexDirection="column">
              <Textarea
                value={phoneticText}
                onChange={(e) => onPhoneticChange(e.target.value)}
                placeholder="Phonetic text is automatically generated when you save changes to the standard narration"
                size="md"
                height="100%"
                resize="none"
                borderColor={borderColor}
                _hover={{ borderColor: 'gray.300' }}
                _focus={{ borderColor: methodistBlue, boxShadow: `0 0 0 1px ${methodistBlue}` }}
              />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default NarrationEditor;
